<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :refresh="refreshTable"
            ajax-path="freeradius/checks"
        />
        <app-modal
            :show.sync="showEditModal"
            width="lg:w-4/5"
            @close="editModalClosed"
        >
            <template slot="header">Edit User</template>
            <template slot="body">
                <app-input
                    v-model="editForm.username"
                    placeholder="Username"
                    :error="editForm.errors.get('username')"
                    @updated="editForm.errors.clear('username')"
                    required
                    focus                  
                />
                <app-input
                    v-model="editForm.value"
                    placeholder="Password"
                    :error="editForm.errors.get('value')"
                    @updated="editForm.errors.clear('value')"
                    required
                    :loading="editFormGeneratingPassword"
                    :action="{
                        icon: () => import('@/icons/Refresh.vue'),
                        label: 'Regenerate Password',
                        handler: editFormGeneratePassword
                    }"                  
                />
                <app-input 
                    type="text"
                    v-model="editForm.mobile_number"
                    placeholder="Mobile Number"
                    :error="editForm.errors.get('mobile_number')"
                    @updated="editForm.errors.clear('mobile_number')"
                    help-text="If provided the account credentials will be sent to this number."
                />                
                <h2 
                    v-if="editForm.replies.length > 0"
                    class="font-thin tracking-widest uppercase mt-6"
                >Replies</h2>
                <div 
                    class="relative"
                    v-for="(attr, index) in editForm.replies"
                >
                    <div class="md:flex md:flex-row md:items-center">
                        <div class="flex items-center md:flex-1">
                            <app-input
                                v-model="editForm.replies[index].attribute"
                                placeholder="Attribute"
                                class="flex-1 mr-2"
                            />
                            <app-select 
                                class="flex-none"
                                v-model="editForm.replies[index].op"
                                placeholder="Operator"
                                :options="['=', ':=', '==', '+=', '!=', '>', '>=', '<', '<=', '=~', '!~', '=*', '!*']"
                            />
                        </div>
                        <div class="flex items-center md:ml-3 md:flex-1">
                            <app-input 
                                class="flex-1"                                
                                v-model="editForm.replies[index].value"
                                placeholder="Value"
                            />
                            <delete-icon 
                                class="text-red-500 cursor-pointer outline-none flex-none ml-3 self-end mb-2"
                                v-tooltip="'Delete'"
                                @click.native="editFormDeleteReply(index)"
                            />
                        </div>
                    </div>                 
                </div>
                <div class="mt-5 flex items-center justify-between">
                    <app-button 
                        @click="editFormAddReply"
                        class="bg-green-500 text-white hover:bg-green-600 mr-3 truncate"
                    >Add Reply</app-button>
                    <div class="flex items-center">
                        <app-button 
                            class="btn-white" 
                            @click="showEditModal = false"
                            :disable="editFormIsLoading"
                        >Cancel</app-button>
                        <app-button 
                            class="btn-blue ml-3" 
                            @click="update"
                            :loading="editFormIsLoading"
                        >Save</app-button>
                    </div>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddModal"
            width="lg:w-1/2"
            @close="addModalClosed"
        >
            <template slot="header">Add User</template>
            <template slot="body">                
                <app-input
                    v-model="addForm.username"
                    placeholder="Username"
                    :error="addForm.errors.get('username')"
                    @updated="addForm.errors.clear('username')"
                    required
                    focus                   
                />
                <app-input
                    v-model="addForm.value"
                    placeholder="Password"
                    :error="addForm.errors.get('value')"
                    @updated="addForm.errors.clear('value')"
                    required
                    :loading="addFormGeneratingPassword"
                    :action="{
                        icon: () => import('@/icons/Refresh.vue'),
                        label: 'Regenerate Password',
                        handler: addFormGeneratePassword
                    }"                  
                />
                <app-input 
                    type="text"
                    v-model="addForm.mobile_number"
                    placeholder="Mobile Number"
                    :error="addForm.errors.get('mobile_number')"
                    @updated="addForm.errors.clear('mobile_number')"
                    help-text="If provided the account credentials will be sent to this number."
                />
                <h2 
                    v-if="addForm.replies.length > 0"
                    class="font-thin tracking-widest uppercase mt-6"
                >Replies</h2>
                <div 
                    class="relative"
                    v-for="(attr, index) in addForm.replies"
                >
                    <div class="md:flex md:flex-row md:items-center">
                        <div class="flex items-center md:flex-1">
                            <app-input
                                v-model="addForm.replies[index].attribute"
                                placeholder="Attribute"
                                class="flex-1 mr-2"
                            />
                            <app-select 
                                class="flex-none"
                                v-model="addForm.replies[index].op"
                                placeholder="Operator"
                                :options="['=', ':=', '==', '+=', '!=', '>', '>=', '<', '<=', '=~', '!~', '=*', '!*']"
                            />
                        </div>
                        <div class="flex items-center md:ml-3 md:flex-1">
                            <app-input 
                                class="flex-1"                                
                                v-model="addForm.replies[index].value"
                                placeholder="Value"
                            />
                            <delete-icon 
                                class="text-red-500 cursor-pointer outline-none flex-none ml-3 self-end mb-2"
                                v-tooltip="'Delete'"
                                @click.native="addFormDeleteReply(index)"
                            />
                        </div>
                    </div>                 
                </div>               
                <div class="mt-5 flex items-center justify-between">
                    <app-button 
                        @click="addFormAddReply"
                        class="bg-green-500 text-white hover:bg-green-600 truncate"
                    >Add Reply</app-button>
                    <div class="flex items-center">
                        <app-button 
                            class="btn-white" 
                            @click="showAddModal = false"
                            :disable="addFormIsLoading"
                        >Cancel</app-button>
                        <app-button 
                            class="btn-blue ml-3" 
                            @click="store"
                            :loading="addFormIsLoading"
                        >Add</app-button>
                    </div>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showUsageModal"
            width="lg:w-5/6"
        >
            <template slot="header">{{ selectedCheck.username }} Usage</template>
            <template slot="body">
                <div class="flex w-full mb-5">
                    <div class="flex-1">
                        <div class="mb-3"><b>This Month</b></div>
                        <table class="table border">
                            <tr>
                                <td>Session Time</td>
                                <td class="text-left">{{ selectedCheck.current_month_usage_for_human.session_time }}</td>
                            </tr>
                            <tr>
                                <td>Upload (MB)</td>
                                <td class="text-left">{{ selectedCheck.current_month_usage_for_human.upload }}</td>
                            </tr>
                            <tr>
                                <td>Download (MB)</td>
                                <td class="text-left">{{ selectedCheck.current_month_usage_for_human.download }}</td>
                            </tr>
                        </table>
                    </div>
                    <div class="flex-1 ml-5">
                        <div class="mb-3"><b>Last Month</b></div>
                        <table class="table border">
                            <tr>
                                <td>Session Time</td>
                                <td class="text-left">{{ selectedCheck.last_month_usage_for_human.session_time }}</td>
                            </tr>
                            <tr>
                                <td>Upload (MB)</td>
                                <td class="text-left">{{ selectedCheck.last_month_usage_for_human.upload }}</td>
                            </tr>
                            <tr>
                                <td>Download (MB)</td>
                                <td class="text-left">{{ selectedCheck.last_month_usage_for_human.download }}</td>
                            </tr>
                        </table>
                    </div>
                </div>
                <tabulator
                    :columns="usageColumns"
                    :ajax-params="usageParams"
                    ajax-path="freeradius/accounts"
                />
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Http from '@/helpers/Http';
    import Form from '@/helpers/Form';
    import debounce from 'lodash/debounce';
    
    export default {
        components: {
            DeleteIcon: () => import('@/icons/Delete.vue'),
            AddIcon: () => import('@/icons/Add.vue'),
        },
        data() {
            return {
                addForm: new Form({
                    username: null,
                    value: null,
                    mobile_number: null,
                    replies: [],
                }),
                editForm: new Form({
                    id: null,
                    username: null,
                    value: null,
                    mobile_number: null,
                    replies: [],
                }),
                addIpForm: new Form({
                    ip: null,
                    username: null,
                }),
                editFormIsLoading: false,
                addFormIsLoading: false,
                addIpFormIsLoading: false,
                tableIsLoading: false,
                showEditModal: false,
                showAddModal: false,
                showAddIpModal: false,
                showUsageModal: false,
                refreshTable: false,
                usageParams: {},
                addFormGeneratingPassword: false,
                editFormGeneratingPassword: false,
                selectedCheck: {
                    current_month_usage_for_human: {},
                    last_month_usage_for_human: {},
                },
                columns: [
                    {
                        title: 'Username',
                        field: 'username',
                        sorter: 'string',
                        minWidth: 350,
                    },
                    {
                        title: 'Password',
                        field: 'value',
                        minWidth: 150,
                    },
                    {
                        title: 'Static IP',
                        field: 'static_ip',
                        minWidth: 250,
                        formatter({_cell}) {
                            if (_cell.value) {
                                return _cell.value;
                            }

                            return '--';
                        }
                    },
                    {
                        title: 'Status',
                        field: 'deleted_at',
                        formatter({_cell}) {
                            if (_cell.value) {
                                return `<span class="badge badge-red">Inactive</span>`;
                            }

                            return `<span class="badge badge-green" onclick="add">Active</span>`;
                        },
                        minWidth: 140,
                    }
                ],
                actions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        handler: checks => {
                            this.showEditModal = true;
                            this.editForm.set(JSON.parse(JSON.stringify(checks[0])));
                        },
                        requiredSelect: true,
                        isEligible(checks) {
                            return !checks[0].deleted_at;
                        }
                    },
                    {
                        label: 'Display Usage',
                        icon: () => import('@/icons/Timeline.vue'),
                        handler: checks => {
                            this.selectedCheck = checks[0];
                            this.showUsageModal = true;
                            this.usageParams = {username: checks[0].username};
                        }
                    },
                    {
                        label: 'Activate',
                        icon: () => import('@/icons/VisibilityOutlined.vue'),
                        requiredSelect: true,
                        handler: checks => {
                            this.activate(checks[0]);
                        },
                        isEligible(checks) {
                            return checks[0].deleted_at;
                        }
                    },
                    {
                        label: 'Deactivate',
                        icon: () => import('@/icons/VisibilityOffOutlined.vue'),
                        requiredSelect: true,
                        handler: checks => {
                            this.deactivate(checks[0]);
                        },
                        isEligible(checks) {
                            return !checks[0].deleted_at;
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        handler: checks => {
                            this.deleteUser(checks[0]);
                        },
                        requiredSelect: true,
                    },                    
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: this.add,
                    }
                ],
                usageColumns: [
                    {
                        title: 'NAS Port ID',
                        field: 'nasportid',
                        sorter: 'string',
                        minWidth: 150,
                        visible: false,
                        download: true,
                    },
                    {
                        title: 'Start Time',
                        field: 'acctstarttime',
                        sorter: 'string',
                        minWidth: 170,
                    },
                    {
                        title: 'Stop Time',
                        field: 'acctstoptime',
                        sorter: 'string',
                        minWidth: 170,
                    },
                    {
                        title: 'Interval',
                        field: 'acctinterval',
                        sorter: 'number',
                        minWidth: 150,
                        visible: false,
                        download: true,
                    },
                    {
                        title: 'Session Time',
                        field: 'acctsessiontime',
                        sorter: 'number',
                        minWidth: 150,
                        formatter({_cell}) {
                            return _cell.row.data.session_time_for_human;
                        }
                    },
                    {
                        title: 'Upload (MB)',
                        field: 'upload_in_mb',
                        sorter: 'number',
                        minWidth: 150,
                    },
                    {
                        title: 'Download (MB)',
                        field: 'download_in_mb',
                        sorter: 'number',
                        minWidth: 160,
                    },
                    {
                        title: 'Terminate Cause',
                        field: 'acctterminatecause',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Framed IP Address',
                        field: 'framedipaddress',
                        sorter: 'string',
                        minWidth: 200,
                    }
                ],
            }
        },
        computed: {
            ...mapGetters({
                items: 'freeradius/checks/items',
            })
        },
        methods: {
            edit(user) {
                this.showEditModal = true;
                this.editForm.set(user);
            },
            editModalClosed() {
                this.editForm.reset();
                this.editForm.errors.clear();                
            },
            update() {
                this.editFormIsLoading = true;
                this.$store.dispatch('freeradius/checks/update', this.editForm.payload())
                    .then(() => {
                        this.editFormIsLoading = false;
                        this.showEditModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'User was updated successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.editFormIsLoading = false;
                        this.editForm.errors.record(response.data);
                    });
            },
            addModalClosed() {
                this.addForm.reset();
                this.addForm.errors.clear();
            },
            add() {
                this.addFormGeneratePassword();
                this.showAddModal = true;
            },
            store() {
                this.addFormIsLoading = true;
                this.$store.dispatch('freeradius/checks/store', this.addForm.payload())
                    .then(() => {
                        this.addFormIsLoading = false;
                        this.showAddModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'User was added successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.addFormIsLoading = false;
                        this.addForm.errors.record(response.data);
                    })
            },
            deactivate(user) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to deactivate "<strong>${user.username}</strong>" account.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, deactivate it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('freeradius/checks/deactivate', user.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'User was successfully deactivated.'
                        })
                    }
                })                
            },
            activate(user) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to activate "<strong>${user.username}</strong>" account.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, activate it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('freeradius/checks/activate', user.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'User was successfully activated.'
                        })
                    }
                })                
            },
            deleteUser(user) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${user.username}</strong>" account permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return this.$store.dispatch('freeradius/checks/delete', user.id);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'User was successfully deleted.'
                        })
                    }
                })                
            },
            search: debounce(function (e) {
                this.$router.push({
                    name: 'freeradius.checks.index',
                    query: {q: this.q}
                })
            }, 1000),
            editFormAddReply() {
                this.editForm.replies.push({
                    attribute: null,
                    op: null,
                    value: null,
                })
            },
            editFormDeleteReply(index) {
                this.editForm.replies.splice(index, 1);
            },
            addFormAddReply() {
                this.addForm.replies.push({
                    attribute: null,
                    op: null,
                    value: null,
                })
            },
            addFormDeleteReply(index) {
                this.addForm.replies.splice(index, 1);
            },
            addFormGeneratePassword() {
                this.addFormGeneratingPassword = true;
                Http.get('password/strong')
                    .then(({data}) => {
                        this.addFormGeneratingPassword = false;
                        this.addForm.set({
                            value: data.password
                        });
                    });
            },
            editFormGeneratePassword() {
                this.editFormGeneratingPassword = true;
                Http.get('password/strong')
                    .then(({data}) => {
                        this.editFormGeneratingPassword = false;
                        this.editForm.set({
                            value: data.password
                        });
                    });
            },
        }
    }
</script>